import isFlagOn from '~/composables/feature-flag'
import { FEATURE_FLAG } from '~/constants/feature-flags.constants'
import memberhubConstants from '~/constants/memberhub.constants'

// Where members go when they reach an email-scraping page with the module off.
export const EMAIL_SCRAPING_OFF_ROUTE = {
  name: 'dashboard-vault-product-form-product-upload',
  query: { vertical: 'energy' }
}

/*
 * Looks the flag up, asking once more when the first answer is off. isFlagOn
 * also answers off when Split times out (e.g. a slow start on the full-page
 * return from Google), and the Split plugin then drops that client, so the
 * retry starts a fresh one. A flag that is really off answers off again
 * straight away, so members without the feature are not held up.
 * @returns {Promise<boolean>}
 */
async function lookUpGmailBillScrapingFlag () {
  const lookUp = () => isFlagOn(FEATURE_FLAG.gmailBillScraping).catch(() => false)
  return (await lookUp()) || lookUp()
}

/*
 * Whether the email-scraping module (feature_GmailBillScraping) is on.
 * Resolved on the client after mount — isFlagOn is always false during SSR —
 * so surfaces stay hidden until it resolves; a check that fails twice counts
 * as off.
 * @returns {{ isEnabled: Ref<boolean>, isResolved: Ref<boolean> }}
 */
export function useGmailBillScrapingFlag () {
  const isEnabled = ref(false)
  const isResolved = ref(false)

  onMounted(async () => {
    isEnabled.value = await lookUpGmailBillScrapingFlag()
    isResolved.value = true
  })

  return { isEnabled, isResolved }
}

/*
 * The connected account the Google callback names (`?account_id=`), kept in
 * the query as the member moves on to imported bills and the review, so the
 * sync-status and drafts requests can be scoped to it. Empty when absent.
 * @returns {ComputedRef<string>}
 */
export function useEmailScrapeAccountId () {
  const route = useRoute()
  return computed(() => {
    const param = route.query?.account_id
    const value = Array.isArray(param) ? param[0] : param
    return typeof value === 'string' ? value : ''
  })
}

/*
 * Page guard for email-scraping pages: keep the page's content behind
 * `isEnabled`, and when the module resolves off, send the member to energy
 * Add Product instead. A page guard rather than route middleware because the
 * flag can't be resolved during SSR, so middleware couldn't gate a direct load.
 * @returns {{ isEnabled: Ref<boolean>, isResolved: Ref<boolean> }}
 */
export function useGmailBillScrapingGuard () {
  const router = useRouter()
  const flag = useGmailBillScrapingFlag()

  watch(flag.isResolved, resolved => {
    if (resolved && !flag.isEnabled.value) {
      router.replace(EMAIL_SCRAPING_OFF_ROUTE)
    }
  })

  return flag
}

const { EMAIL_SCRAPING_STATUS } = memberhubConstants

/*
 * What emailScrapingOutcome returns: an email_scraping_status (CLUB-480,
 * memberhubConstants.EMAIL_SCRAPING_STATUS), or one of two frontend-only
 * outcomes: SYNC_COMPLETE (an older API with no status, whose sync has
 * finished) and NONE (no inbox to go on).
 */
export const EMAIL_SCRAPING_OUTCOME = Object.freeze({
  ...EMAIL_SCRAPING_STATUS,
  SYNC_COMPLETE: 'sync_complete',
  NONE: 'none'
})

// One inbox: its status, an unknown one read as still processing; with no
// status at all (an older API), its sync_complete.
function inboxScrapingOutcome (inbox) {
  const status = inbox.scrapingStatus
  if (status == null) {
    return inbox.syncComplete ? EMAIL_SCRAPING_OUTCOME.SYNC_COMPLETE : EMAIL_SCRAPING_OUTCOME.PROCESSING
  }
  return Object.values(EMAIL_SCRAPING_STATUS).includes(status) ? status : EMAIL_SCRAPING_OUTCOME.PROCESSING
}

/*
 * What the member's inboxes say about the email scan: the account from the
 * Google callback when there is one, else every active inbox. Processing
 * while any of them is; then bills found when any found bills; then (older
 * API) sync complete; else no bills found. NONE when there is no inbox to go
 * on. Shared by the processing and imported-bills pages.
 * @param {Array<{id: number|string, isActive: boolean, syncComplete: boolean, scrapingStatus: string|null}>} connections - useConnectedAccounts().connections
 * @param {string} [accountId]
 * @returns {string} an EMAIL_SCRAPING_OUTCOME value
 */
export function emailScrapingOutcome (connections, accountId = '') {
  const inboxes = accountId
    ? connections.filter(inbox => String(inbox.id) === String(accountId))
    : connections.filter(inbox => inbox.isActive)
  if (!inboxes.length) return EMAIL_SCRAPING_OUTCOME.NONE
  const outcomes = inboxes.map(inboxScrapingOutcome)
  if (outcomes.includes(EMAIL_SCRAPING_OUTCOME.PROCESSING)) return EMAIL_SCRAPING_OUTCOME.PROCESSING
  if (outcomes.includes(EMAIL_SCRAPING_OUTCOME.BILLS_FOUND)) return EMAIL_SCRAPING_OUTCOME.BILLS_FOUND
  if (outcomes.includes(EMAIL_SCRAPING_OUTCOME.SYNC_COMPLETE)) return EMAIL_SCRAPING_OUTCOME.SYNC_COMPLETE
  return EMAIL_SCRAPING_OUTCOME.NO_BILLS_FOUND
}

const GMAIL_FAILURE_STATUSES = ['denied', 'insufficient_scope', 'error']
const GMAIL_STATUSES = ['connected', ...GMAIL_FAILURE_STATUSES]

// The vault context the email-connect callback can carry back to the processing page.
const EMAIL_CONNECT_CONTEXT_KEYS = ['vertical', 'completion_source']
const EMAIL_CONNECT_CONTEXT_VALUE = /^[a-z0-9_-]{1,40}$/

/*
 * A vertical or completion_source from the email-connect callback (web redirect
 * or app link), or undefined for anything other than a short lowercase slug
 * such as energy or vault_upload: either URL is external input. A repeated
 * query key arrives as an array; the first entry counts.
 * @param {unknown} value
 * @returns {string|undefined}
 */
export function emailConnectContextValue (value) {
  const entry = Array.isArray(value) ? value[0] : value
  return typeof entry === 'string' && EMAIL_CONNECT_CONTEXT_VALUE.test(entry) ? entry : undefined
}

// The parsed compareclub(-dev)://email-connect/callback link, or null for any other.
function emailConnectAppLink (urlString) {
  let url
  try {
    url = new URL(urlString)
  } catch {
    return null
  }
  const isAppScheme = url.protocol === 'compareclub:' || url.protocol === 'compareclub-dev:'
  return isAppScheme && url.hostname === 'email-connect' && url.pathname.startsWith('/callback') ? url : null
}

/*
 * Whether a link the app was opened with is the email-connect callback.
 * @param {string} urlString
 * @returns {boolean}
 */
export const isEmailConnectAppLink = urlString => !!emailConnectAppLink(urlString)

/*
 * Native app (CLUB-480): where to take the member when Google's result comes
 * back to the app, or null for any other link. The callback opens
 * compareclub(-dev)://email-connect/callback with a completion_code (finished
 * here through `complete`, as the signed-in member) or a failure gmail_status.
 * Either way the member lands on the processing page with gmail_status (only a
 * known one, anything else is error), a numeric account_id when the
 * completion returns one, and the vertical/completion_source the link carries
 * (CLUB-961).
 * @param {string} urlString - the deep link the app was opened with
 * @param {(code: string) => Promise<{ gmailStatus: string, accountId: string }>} complete - useGmailConsent().completeGmailConnect
 * @returns {Promise<string|null>}
 */
export async function emailConnectAppPath (urlString, complete) {
  const url = emailConnectAppLink(urlString)
  if (!url) return null

  // The link is external input (any app or page can open a custom scheme), and
  // the callback only ever sends a completion code or a failure status. So
  // connected and the account id come only from the authenticated completion;
  // from the link itself, only a failure status is taken, plus the vault
  // context as slugs.
  const completionCode = url.searchParams.get('completion_code')?.trim()
  const linkStatus = url.searchParams.get('gmail_status')
  const result = completionCode
    ? await complete(completionCode)
    : { gmailStatus: GMAIL_FAILURE_STATUSES.includes(linkStatus) ? linkStatus : 'error', accountId: '' }

  const params = new URLSearchParams({ gmail_status: GMAIL_STATUSES.includes(result?.gmailStatus) ? result.gmailStatus : 'error' })
  if (/^\d+$/.test(String(result?.accountId || ''))) params.set('account_id', String(result.accountId))
  for (const key of EMAIL_CONNECT_CONTEXT_KEYS) {
    const value = emailConnectContextValue(url.searchParams.get(key))
    if (value) params.set(key, value)
  }
  return `/dashboard/vault/form/product/processing-emails?${params}`
}

/*
 * Native app (CLUB-480): handles the email-connect callback link behind the page
 * loader. The member comes back from the browser sheet onto the consent page, so
 * the loader goes up first and stays until the processing page is shown:
 * close the sheet, finish the connect (emailConnectAppPath), open the processing
 * page. Returns false, doing nothing, for any other link.
 * @param {string} urlString
 * @param {{
 *   complete: (code: string) => Promise<{ gmailStatus: string, accountId: string }>,
 *   closeBrowser: () => Promise<void>,
 *   navigate: (path: string) => Promise<void>,
 *   pageLoader: { setPageLoader: (on: boolean) => void },
 *   nextPageShown: () => Promise<void>
 * }} deps - nextPageShown resolves once the next page has rendered
 * @returns {Promise<boolean>}
 */
export async function finishEmailConnectInApp (urlString, { complete, closeBrowser, navigate, pageLoader, nextPageShown }) {
  if (!isEmailConnectAppLink(urlString)) return false

  pageLoader.setPageLoader(true)
  try {
    await closeBrowser()
    const path = await emailConnectAppPath(urlString, complete)
    const pageShown = nextPageShown()
    await navigate(path)
    await pageShown
  } finally {
    pageLoader.setPageLoader(false)
  }
  return true
}
